import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  FormattedMessage,
  FormattedNumber,
  injectIntl,
  intlShape
} from 'react-intl';
import formatTimestamp from '../../../i18n/formatTimestamp';

import {
  Button,
  Card,
  DataTable,
  DataTableCell,
  DataTableColumn,
  Dropdown,
  Input,
  InputIcon,
  MediaObject,
  Spinner,
  Toast,
  ToastContainer,
  Tooltip
} from '@salesforce/design-system-react';

import './AttributeList.scss';
import {
  AttributeEditModal,
  NoAttributes,
  WeatherAttributeEditModal
} from '../';
import * as Util from '../../../common/utilities';
import { withSession } from '../../../i18n/containers/AppInit.js';

const CustomDataTableCell = ({ children, property, ...props }) => {
  switch (property) {
    case 'required':
      const i18nKey = `components.ecs.attributes.${children}`;
      return (
        <td className={`attribute-cell wrap-text`}>
          <FormattedMessage id={i18nKey} />
        </td>
      );
    case 'values':
      return (
        <td className={`attribute-cell wrap-text`}>
          <FormattedNumber value={children || 0} />
        </td>
      );
    case 'actions':
      const disabled = props.item.in_use;
      // The tooltip needs undefined for isOpen value, in order to open on hover
      const open = disabled ? undefined : false;
      const actions = [
        {
          label: props.formatMessage({
            id: 'components.ecs.assetBrowser.menu.edit'
          }),
          value: 'edit'
        }
      ];

      actions.push({
        disabled,
        label: props.formatMessage({
          id: 'components.ecs.assetBrowser.menu.delete'
        }),
        tooltipContent: props.formatMessage({
          id: 'components.ecs.attributes.deleteDisabled'
        }),
        value: 'delete'
      });

      const dropDown = (
        <Dropdown
          assistiveText={{ icon: 'Attribute Options' }}
          buttonVariant="icon"
          iconCategory="utility"
          iconName="down"
          iconVariant="border-filled"
          align="right"
          className="actions-dropdown"
          width="xx-small"
          options={actions}
          onSelect={({ value }) => props.onAction(value, props.item, disabled)}
          tooltipMenuItem={
            <Tooltip
              id="drop-down-tooltip"
              content={props.formatMessage({
                id: 'components.ecs.attributes.deleteDisabled'
              })}
              align="left"
              position="overflowBoundaryElement"
              isOpen={open}
            />
          }
        />
      );
      return <td className={`attribute-cell wrap-text`}>{dropDown}</td>;
    default: {
      return <td className={`attribute-cell wrap-text`}>{children}</td>;
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  property: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object
};

class AssetAttributeList extends Component {
  getI18nID(suffix) {
    return `components.ecs.attributes.${suffix}`;
  }

  headerCell(property, formatMessage) {
    return formatMessage({ id: this.getI18nID(property) });
  }

  columns() {
    const cols = {
      name: { sortable: false, width: '300px' },
      isRequired: { sortable: false, width: '200px' }
    };
    if (this.props.rules?.settings?.weather) {
      cols.isWeather = { sortable: false, width: '200px' };
    }
    cols.isMultiValue = { sortable: false, width: '200px' };
    cols.valueCount = { sortable: false, width: '150px' };
    cols.updatedAt = { sortable: false, width: '215px' };

    if (!this.props.flags.isReadonly) {
      cols.actions = { sortable: false };
    }
    return cols;
  }

  tableData(items, filter) {
    const filtered = filter?.name
      ? items.filter(item =>
          item.name.toUpperCase().includes(filter.name.toUpperCase())
        )
      : items;
    const { formatMessage } = this.props.intl;
    const Y = formatMessage({ id: this.getI18nID('Y') });
    const N = formatMessage({ id: this.getI18nID('N') });
    return filtered.map(item => ({
      ...item,
      id: item.uuid,
      isRequired: item.required ? Y : N,
      isWeather: item.type.startsWith('Weather') ? Y : N,
      isMultiValue: item.multi_value ? Y : N,
      valueCount: item.values?.length || 0,
      updatedAt: formatTimestamp(this.props.intl)(item.updated_at)
    }));
  }

  maxAttributes() {
    return 30;
  }

  header(items) {
    const count = items ? items.length : 0;
    const maxAttributes = this.maxAttributes();

    return (
      <div className="attribute-header">
        <h2>
          <FormattedMessage id={this.getI18nID('assetHeader')} />
        </h2>
        <FormattedMessage
          id={this.getI18nID('items')}
          values={{ count, maxAttributes }}
        />
      </div>
    );
  }

  onNewAttributeMenuSelect({ value }) {
    if (value === 'newStandardAttribute') {
      this.openAttributeModal();
    } else if (value === 'newWeatherAttribute') {
      this.openWeatherAttributeModal();
    } else {
      console.warn('Unexpected menu option', value);
    }
  }

  openAttributeModal(attr) {
    const attribute = attr || { values: [''], allow_new: true };
    this.props.actions.openModal('attributeEdit', {
      attribute,
      original: {
        ...attribute,
        values: [...attribute.values]
      }
    });
  }

  openWeatherAttributeModal(attr) {
    const attribute = attr || { values: [''], allow_new: true };
    this.props.actions.openModal('weatherAttributeEdit', {
      attribute,
      original: {
        ...attribute,
        values: [...attribute.values],
        details: { ...attribute.details }
      }
    });
  }

  handleAttributeSave(session) {
    return attr => {
      this.props.actions.saveAttribute(attr, {
        callback: this.props.actions.getAttributes,
        cToken: session.cToken
      });

      const name = this.props.modals.weatherAttributeEdit?.isOpen
        ? 'weatherAttributeEdit'
        : 'attributeEdit';
      this.props.actions.closeModal(name);
    };
  }

  headerActions() {
    const { formatMessage } = this.props.intl;
    const searchPlaceholder = formatMessage({
      id: 'components.ecs.attributes.searchPlaceholder'
    });
    const count = this.props.attributes ? this.props.attributes.length : 0;
    const maxAttributesReached = count >= this.maxAttributes();
    const assetAttributeOnly = (
      <Button
        label={formatMessage({
          id: this.getI18nID('newAttribute')
        })}
        onClick={() => this.openAttributeModal()}
        disabled={this.props.flags.isReadonly || maxAttributesReached}
      />
    );
    const weatherAttribute = (
      <Dropdown
        align="center"
        iconCategory="utility"
        iconName="down"
        iconPosition="right"
        width="x-small"
        label={formatMessage({
          id: this.getI18nID('newAttribute')
        })}
        disabled={this.props.flags.isReadonly || maxAttributesReached}
        onSelect={this.onNewAttributeMenuSelect.bind(this)}
        options={[
          {
            label: formatMessage({
              id: this.getI18nID('newStandardAttribute')
            }),
            value: 'newStandardAttribute'
          },
          {
            label: formatMessage({
              id: this.getI18nID('newWeatherAttribute')
            }),
            value: 'newWeatherAttribute'
          }
        ]}
      />
    );
    return (
      <div className="attribute-header-actions">
        <Input
          className="filter-by-name"
          iconLeft={
            <InputIcon
              assistiveText={{
                icon: 'Search'
              }}
              name="search"
              category="utility"
            />
          }
          onChange={(evt, { value }) =>
            this.props.actions.updateAttributeFilter({ name: value })
          }
          placeholder={searchPlaceholder}
        />
        {this.props.rules?.settings?.weather
          ? weatherAttribute
          : assetAttributeOnly}
      </div>
    );
  }

  drawSpinner(isLoading) {
    return isLoading ? (
      <div className="pi-common-spinner">
        <Spinner variant="brand" />
      </div>
    ) : null;
  }

  renderNoData() {
    return (
      <Card
        className="attribute-card"
        header={<MediaObject body={this.header([])} />}
        headerActions={this.headerActions()}
      >
        <NoAttributes intl={this.props.intl} attributeType="asset" />
      </Card>
    );
  }

  renderTable(items) {
    const { formatMessage } = this.props.intl;

    return (
      <Card
        className="attribute-card"
        header={<MediaObject body={this.header(items)} />}
        headerActions={this.headerActions()}
      >
        <div className="attribute-list-div">
          <DataTable
            items={items}
            className="attribute-list-table"
            fixedLayout
            fixedHeader
            noRowHover
          >
            {Object.entries(this.columns()).map((column, index) => {
              const sortable = column[1].sortable;
              const width = column[1].width;
              const property = column[0];
              return (
                <DataTableColumn key={index} sortable={sortable}>
                  <CustomDataTableCell
                    property={property}
                    formatMessage={formatMessage}
                    label={this.headerCell(property, formatMessage)}
                    width={width}
                    onAction={this.props.onListAction}
                  />
                </DataTableColumn>
              );
            })}
          </DataTable>
        </div>
      </Card>
    );
  }

  componentDidUpdate(prevProps) {
    if (Util.propChanged(prevProps, this.props, 'lastSave')) {
      const { lastSave } = this.props;

      if (lastSave.error) {
        this.props.actions.openToast('apiError', {
          ...lastSave.error
        });
      } else {
        this.props.actions.openToast('attributes', {
          name: lastSave.attribute?.name,
          mode: lastSave.mode
        });
      }
    }
  }

  toast() {
    const { formatMessage } = this.props.intl;
    if (this.props.toast.attributes) {
      const heading = formatMessage(
        {
          id: `components.ecs.attributes.toast.${this.props.toast.attributes.mode}`
        },
        { name: this.props.toast.attributes.name }
      );
      return (
        <Toast
          labels={{ heading }}
          variant="success"
          duration={10000}
          onRequestClose={() => this.props.actions.closeToast('attributes')}
        />
      );
    }

    return null;
  }

  render() {
    const { attributes, filter, isLoading } = this.props;

    return withSession(session => {
      return (
        <React.Fragment>
          {this.drawSpinner(isLoading)}
          {!isLoading && !attributes?.length
            ? this.renderNoData()
            : this.renderTable(this.tableData(attributes, filter))}
          <ToastContainer>{this.toast()}</ToastContainer>
          <AttributeEditModal
            actions={this.props.actions}
            modals={this.props.modals}
            onSave={this.handleAttributeSave(session)}
            attributeType="asset"
            flags={this.props.flags}
          />
          <WeatherAttributeEditModal
            actions={this.props.actions}
            modals={this.props.modals}
            onSave={this.handleAttributeSave(session)}
            attributeType="weather"
            flags={this.props.flags}
            rules={this.props.rules}
          />
        </React.Fragment>
      );
    });
  }
}

AssetAttributeList.propTypes = {
  attributes: PropTypes.array.isRequired,
  filter: PropTypes.object,
  flags: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  lastSave: PropTypes.object,
  onListAction: PropTypes.func.isRequired,
  modals: PropTypes.object.isRequired,
  rules: PropTypes.object,
  toast: PropTypes.object,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(AssetAttributeList);
